import { muiDataGridStarted } from "../apiutils";
import { apiSlice } from "../createApiSlice";

const INVOICEPRODUCT_BASE = "/invoiceproducts";
const buildUrl = (endpoint) => INVOICEPRODUCT_BASE + endpoint;
const API_TAG = 'InvoiceProducts';

const invoiceProductSlice =  apiSlice.injectEndpoints({
    endpoints: builder => ({
        getInvoiceProducts: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: () => [{ type: API_TAG }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getInvoiceProducts', API_TAG)
        }),
        getInvoiceProductsSelect: builder.query({
            query: (type) => ({
                url: buildUrl(`/select${type ? `?subtype=${type}`: ""}`),
                method: 'GET',
            }),
            providesTags: () => [{ type: API_TAG }],
        }),
        getInvoiceProductsSelectWithSearch: builder.query({
            query: searchBody => ({
                url: buildUrl(`/select`),
                method: 'POST',
                body: searchBody
            }),
            providesTags: () => [{ type: API_TAG }],
        }),
        getInvoiceProductCategories: builder.query({
            query: (id) => ({
                url: buildUrl(`/${id}/categories`),
                method: 'GET'
            }),
            providesTags: () => [{ type: API_TAG }],
        }),
        getInvoiceProductShipperCosts: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`/${args.id}/shippercosts?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: () => [{ type: API_TAG }, { type: 'CostsShipper' }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getInvoiceProductShipperCosts', API_TAG)
        }),
        getInvoiceProduct: builder.query({
            query: (id) => ({
                url: buildUrl(`/${id}`),
                method: 'GET',
            }),
            // providesTags: (id) => [{ type: `CountingStrategyTemplateSingle`,id }]
            providesTags: (result, error, arg) => [{ type: `${API_TAG}Single`, id: arg }]
        }),
        addInvoiceProduct: builder.mutation({
            query: invoiceProductInfo => ({
                url: buildUrl(`/new`),
                method: 'POST',
                body: invoiceProductInfo,
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG}],
        }),
        updateInvoiceProduct: builder.mutation({
            query: invoiceProductInfo => ({
                url: buildUrl(`/${invoiceProductInfo.id}`),
                method: 'PUT',
                body: invoiceProductInfo,
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG }, { type: `${API_TAG}Single`, id: arg.id }],
        }),
        updateInvoiceProductPrices: builder.mutation({
            query: invoiceProductInfo => ({
                url: buildUrl(`/setprices`),
                method: 'PUT',
                body: invoiceProductInfo,
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG }, { type: `${API_TAG}Single`, id: arg.id }, {type: 'InvoiceCategories'}],
        }),
        updateInvoiceCategoryProductTiers: builder.mutation({
            query: ({ invoiceCategoryProductId, tiers }) => ({
                url: buildUrl(`/categoryproducts/${invoiceCategoryProductId}/tiers`),
                method: 'PUT',
                body: { tiers },
            }),
            invalidatesTags: () => [{type: API_TAG }],
        }),
        deleteInvoiceProduct: builder.mutation({
            query: (id) => ({
                url: buildUrl(`/${id}`),
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG }, { type: `${API_TAG}Single`, id: arg.id }],
        }),
        exportInvoiceProducts: builder.mutation({
            query: () => ({
                url: buildUrl(`/export`),
                method: 'POST',
                body: {},
                responseHandler: async (response) => {
                    if (!response.ok) {
                        throw new Error('Network response was not ok');
                    }

                    const contentType = response.headers.get('content-type');

                    // Check if response is JSON (status 202 - processing or 200 with blobUrl)
                    if (contentType && contentType.includes('application/json')) {
                        const jsonData = await response.json();

                        // If immediate download is ready (status 200 with blobUrl)
                        if (jsonData.blobUrl) {
                            // For storage URLs or signed URLs, just trigger direct download
                            // No need to fetch again - the URL is already accessible
                            const a = document.createElement('a');
                            a.href = jsonData.blobUrl;
                            a.download = jsonData.fileName || `invoiceproducts_export_${new Date().toISOString().split('T')[0]}.xlsx`;
                            a.target = '_blank'; // Open in new tab as fallback
                            document.body.appendChild(a);
                            a.click();
                            a.remove();

                            return { success: true, message: jsonData.message, immediate: true };
                        }

                        // Background processing (status 202)
                        return { success: true, processing: true, message: jsonData.message, exportId: jsonData.exportId };
                    }

                    // Legacy support: Direct blob response (for backwards compatibility)
                    const blob = await response.blob();

                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `invoiceproducts_export_${new Date().toISOString().split('T')[0]}.xlsx`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);

                    return { success: true, immediate: true };
                },
            }),
        }),
    })
});

export const {
    useGetInvoiceProductsQuery,
    useGetInvoiceProductsSelectQuery,
    useGetInvoiceProductsSelectWithSearchQuery,
    useGetInvoiceProductCategoriesQuery,
    useGetInvoiceProductShipperCostsQuery,
    useGetInvoiceProductQuery,
    useAddInvoiceProductMutation,
    useUpdateInvoiceProductMutation,
    useUpdateInvoiceProductPricesMutation,
    useUpdateInvoiceCategoryProductTiersMutation,
    useDeleteInvoiceProductMutation,
    useExportInvoiceProductsMutation
  } = invoiceProductSlice;
